import {
  X,
  MessageSquare,
  Mail,
  PhoneCall,
  Bell,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2,
  Calendar,
  User,
  History,
} from 'lucide-react';
import { useCrmOutreachHistory } from '../../hooks/useCrm';
import { useLanguage } from '../../lib/LanguageContext';
import { copy } from '../../copy/copy';
import { Bilingual } from '../Bilingual';
import type { CrmParticipantItem, OutreachChannel, OutreachStatus } from '@lms/shared/types';

interface OutreachHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: CrmParticipantItem;
}

export function OutreachHistoryModal({ isOpen, onClose, participant }: OutreachHistoryModalProps) {
  const { language } = useLanguage();
  const { data: history, isLoading } = useCrmOutreachHistory(participant.id);

  if (!isOpen) return null;

  const logs = history || [];

  const getChannelIcon = (channel: OutreachChannel) => {
    switch (channel) {
      case 'WHATSAPP':
        return <MessageSquare size={16} className="text-emerald-600" />;
      case 'EMAIL':
        return <Mail size={16} className="text-sky-600" />;
      case 'PHONE':
        return <PhoneCall size={16} className="text-primary" />;
      default:
        return <Bell size={16} className="text-amber-600" />;
    }
  };

  const getStatusBadge = (status: OutreachStatus) => {
    switch (status) {
      case 'RESOLVED':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-headline font-bold bg-success/15 text-success flex items-center gap-1">
            <CheckCircle2 size={12} />
            <span>Selesai / Tuntas</span>
          </span>
        );
      case 'REPLIED':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-headline font-bold bg-sky-500/15 text-sky-600 flex items-center gap-1">
            <Clock size={12} />
            <span>Merespons</span>
          </span>
        );
      case 'NO_RESPONSE':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-headline font-bold bg-error/15 text-error flex items-center gap-1">
            <AlertTriangle size={12} />
            <span>Tidak Ada Respons</span>
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-headline font-bold bg-surface-container-high text-on-surface-variant flex items-center gap-1">
            <Clock size={12} />
            <span>Terkirim</span>
          </span>
        );
    }
  };

  const formatDate = (dateStr: string | Date) => {
    return new Date(dateStr).toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fadeIn overflow-y-auto py-10">
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-2xl shadow-level4 overflow-hidden animate-slideUp border border-on-surface/5 my-auto">
        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-outline-variant/10 bg-surface-container-low/40 flex justify-between items-start">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-headline font-bold uppercase tracking-wider mb-1">
              <History size={12} />
              <span>Log Interaksi CRM</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
              <Bilingual {...copy.crm.historyModalTitle} />
            </h2>
            <p className="text-xs font-body text-on-surface-variant m-0 pt-1">
              Peserta: <strong className="text-on-surface">{participant.name}</strong> (
              {participant.nip || participant.email})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors text-on-surface-variant"
            aria-label="Tutup modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content List */}
        <div className="p-6 sm:p-8 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <span className="text-xs font-body text-on-surface-variant">
                Memuat riwayat interaksi...
              </span>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <div className="w-12 h-12 rounded-full bg-surface-container-high mx-auto flex items-center justify-center text-on-surface-variant opacity-60">
                <History size={24} />
              </div>
              <p className="text-sm font-body text-on-surface-variant m-0">
                <Bilingual {...copy.crm.noHistoryYet} />
              </p>
            </div>
          ) : (
            <div className="relative pl-6 border-l-2 border-outline-variant/15 space-y-6">
              {logs.map(log => (
                <div key={log.id} className="relative group">
                  {/* Timeline Dot */}
                  <div className="absolute -left-[31px] top-1 w-6 h-6 rounded-full bg-surface-container-lowest border-2 border-primary flex items-center justify-center shadow-xs">
                    {getChannelIcon(log.channel)}
                  </div>

                  {/* Log Card */}
                  <div className="p-4 rounded-2xl bg-surface-container-low/60 border border-outline-variant/10 space-y-2 hover:bg-surface-container-low transition-colors">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-headline font-bold text-on-surface uppercase tracking-wider">
                          {log.channel}
                        </span>
                        {log.program && (
                          <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-mono font-bold">
                            {log.program.code}
                          </span>
                        )}
                      </div>
                      {getStatusBadge(log.status)}
                    </div>

                    {log.notes && (
                      <p className="text-xs font-body text-on-surface leading-relaxed m-0 whitespace-pre-wrap bg-surface-container-lowest/50 p-3 rounded-xl border border-outline-variant/5">
                        {log.notes}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-outline-variant/10 text-[11px] text-on-surface-variant">
                      <div className="flex items-center gap-1.5 font-medium">
                        <User size={12} className="opacity-60" />
                        <span>Oleh: {log.admin?.name || 'Admin TSL'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 font-mono text-[10px]">
                        <Calendar size={12} className="opacity-60" />
                        <span>{formatDate(log.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:px-8 border-t border-outline-variant/10 bg-surface-container-low/30 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-surface-container-high hover:bg-surface-container text-on-surface text-xs font-bold transition-colors"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}
